"use client";

import { cn } from "@/shared/lib/utils";
import type { ChartConfig } from "./ChartContainer";

type TooltipPayloadItem = {
  dataKey?: string;
  name?: string;
  value?: string | number;
  color?: string;
  fill?: string;
};

type ChartTooltipContentProps = {
  active?: boolean;
  payload?: ReadonlyArray<TooltipPayloadItem>;
  label?: React.ReactNode;
  /** Indicator style next to each item: "line" (vertical bar) or "dot" */
  indicator?: "line" | "dot";
  /** Hide the label row (e.g. x-axis value) */
  hideLabel?: boolean;
  /** Chart config for display names (config[dataKey].label) */
  config?: ChartConfig;
  /** Custom class for wrapper */
  className?: string;
};

export function ChartTooltipContent({
  active,
  payload = [],
  label,
  indicator = "line",
  hideLabel = false,
  config,
  className,
}: ChartTooltipContentProps) {
  if (!active || payload.length === 0) return null;

  return (
    <div
      className={cn(
        "rounded-md border bg-background px-3 py-2 text-sm shadow-md",
        className,
      )}
    >
      {!hideLabel && label != null && (
        <p className="mb-2 font-medium text-foreground leading-none">{label}</p>
      )}
      <div className="flex flex-col gap-1.5">
        {payload.map((item, index) => {
          const color = item.color ?? item.fill ?? "var(--chart-1)";
          const displayName =
            (item.dataKey && config?.[String(item.dataKey)]?.label) ??
            item.name ??
            item.dataKey ??
            "";
          const value =
            typeof item.value === "number"
              ? item.value.toLocaleString()
              : String(item.value ?? "");

          return (
            <div
              key={item.dataKey ?? index}
              className="flex items-center gap-2"
            >
              {indicator === "line" && (
                <span
                  className="h-4 w-0.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
              )}
              {indicator === "dot" && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
              )}
              <span className="shrink-0 text-muted-foreground">
                {displayName}:
              </span>
              <span className="font-medium text-foreground tabular-nums">
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
